import React, { useCallback, useRef } from 'react';
import { Link } from 'expo-router';
import { Pressable, StyleSheet, View } from 'react-native';

import { theme, usePressableState, webOnlyStyles } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { type GiftCardsGridItemLinkProps } from './GiftCardsGridItemLink.types';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardsGridItemLink = (props: GiftCardsGridItemLinkProps) => {
  const { children } = props;

  const cardRef = useRef<View>(null);
  // @ts-expect-error - React ref type mismatch
  const { isHovered, isFocused } = usePressableState(cardRef);

  // ─── Styles ──────────────────────────────────────────────────────────

  const gridItemPressableContainerStyles = [
    gridItemPressableContainerWebStyles,
    isHovered || isFocused ? styles.pressableContainerHover : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={gridItemPressableContainerStyles}>
      <View ref={cardRef}>
        <GiftCardsGridItemLinkPressable {...props}>{children}</GiftCardsGridItemLinkPressable>
      </View>
    </View>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const GiftCardsGridItemLinkPressable = (props: GiftCardsGridItemLinkProps) => {
  const { id, coverSrc, children, accessibilityLabel, isSignedIn, onSignedOutCardPress } = props;

  const { track } = useTelemetry();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const trackGiftCardLinkPress = useCallback(() => {
    track('gift.skin_tapped', { skinID: id, skinURL: coverSrc });
  }, [coverSrc, id, track]);

  const onSignedOutGiftCardLinkPress = useCallback(() => {
    trackGiftCardLinkPress();
    onSignedOutCardPress();
  }, [onSignedOutCardPress, trackGiftCardLinkPress]);

  // ─────────────────────────────────────────────────────────────────────

  const pressableStyles = [gridItemPressableWebStyles, styles.pressable];

  if (!isSignedIn) {
    return (
      <Pressable
        aria-label={accessibilityLabel}
        accessibilityRole="button"
        aria-haspopup="dialog"
        style={pressableStyles}
        onPress={onSignedOutGiftCardLinkPress}
      >
        {children}
      </Pressable>
    );
  }

  return (
    <Link
      aria-label={accessibilityLabel}
      style={pressableStyles}
      onPress={trackGiftCardLinkPress}
      href={`/gift/checkout/${id}`}
    >
      {children}
    </Link>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressableContainerHover: {
    transform: [{ scale: 1.03 }],
    opacity: 0.9,
  },
  pressable: {
    display: 'flex',
    flexDirection: 'column',
  },
});

// ─── Web Styles ──────────────────────────────────────────────────────────────

const gridItemPressableContainerWebStyles = webOnlyStyles({
  willChange: 'transform, opacity',
  transition: `transform ${theme.transitions.base}ms ease-in-out, opacity ${theme.transitions.base}ms ease-in-out`,
});

const gridItemPressableWebStyles = webOnlyStyles({
  outlineOffset: 8,
});
